import { HydratedMetric } from "@/features/Metrics/HydratedMetric";
import { asOfDateAtom } from "@/features/Metrics/state/Metrics.state";
import { useAtomValue, useSetAtom } from "jotai";
import { EmptyStateCard } from "../components/EmptyStateCard";
import { AIMContextViewAtom } from "./AIMPanelContext";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import clsx from "clsx";
import { useMemo } from "react";
import { CalcResult } from "../components/CalcResult";

export function LTMLineagePanel({ metric }: { metric: HydratedMetric | null }) {
    const setAIMContextView = useSetAtom(AIMContextViewAtom);

    const description = useMemo(() => {
        const fullDescription = metric?.rawData.description?.description;
        if (!fullDescription) return undefined;
        const parts = fullDescription.split("\n\n");
        const out = parts.length >= 3 ? parts[1] : fullDescription;
        return out;
    }, [metric?.rawData]);

    if (!metric)
        return (
            <EmptyStateCard
                heading="Internal Error - Metric not found"
                body="Unable to show lineage for this metric."
            />
        );

    return (
        <div
            className={clsx(
                "px-6 pb-6",
                "custom-scrollbar absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-4 flex flex-col">
                <RightSidebarButton
                    onClick={() => {
                        setAIMContextView(null);
                    }}
                />
                {metric && (
                    <>
                        <h2 className="pageheading ">{metric.label}</h2>
                        <p className="body-s italic text-gray-400">
                            {description}
                        </p>
                    </>
                )}
            </div>
            <div className="custom-scrollbar hide-scrollbar overflow-y-scroll">
                <LTMCalculation metric={metric} />
            </div>
        </div>
    );
}

function LTMCalculation({ metric }: { metric: HydratedMetric }) {
    const asOfDate = useAtomValue(asOfDateAtom);
    const formula = metric.getLTMFormula(asOfDate);

    if (!formula) {
        return (
            <EmptyStateCard
                heading="LTM Not Available"
                body="LTM calculation is only available for monthly resolution metrics."
            />
        );
    }

    return (
        <div>
            <h3 className="heading-m mb-3">Last Twelve Months (LTM)</h3>
            <span
                className={clsx(
                    "flex flex-wrap",
                    "formula-display items-center gap-y-2",
                )}
            >
                {formula.formula.map((part, index) =>
                    ["(", ")", "/", "+", "12"].includes(part) ? (
                        <span key={`ltm-${index}`} className="py-0.5 pr-2">
                            {part}
                        </span>
                    ) : (
                        <span
                            key={`ltm-${index}`}
                            className={clsx(
                                "text-default mr-2 px-2 py-0.5",
                                part.includes("Missing")
                                    ? "rounded-lg border border-dashed border-gray-500 text-gray-400"
                                    : "border-default rounded-lg border",
                            )}
                            style={
                                !part.includes("Missing")
                                    ? { color: "white" }
                                    : undefined
                            }
                        >
                            {part}
                        </span>
                    ),
                )}
                {formula.formula.length > 1 && (
                    <CalcResult value={formula.total} />
                )}
            </span>
        </div>
    );
}
